import { useEffect, useRef, useState } from "react";
import { Popconfirm, Select } from "antd";
import MondayButton from "../../commonComponents/MondayButton/MondayButton";
import MessageBubble from "./MessageBubble";
import ChatComposer from "./ChatComposer";
import {
  TICKET_STATUSES,
  getTicketMessages,
  isElevatedRole,
  ticketStatusStyle,
} from "./utils/ticketStatus";
import { isEmptyChatHtml } from "./utils/chatContent";
import { setFocusedSupportTicket } from "./utils/supportFocus";

function TicketThread({
  ticket,
  userRole,
  employeeId,
  sending,
  onSend,
  onStatusChange,
  onComplete,
  onDelete,
}) {
  const [draft, setDraft] = useState("");
  const bottomRef = useRef(null);
  const isAdmin = isElevatedRole(userRole);
  const messages = getTicketMessages(ticket);
  const completed = ticket?.ticketStatus === "Completed";
  const canReply = !!ticket && !completed;

  useEffect(() => {
    setDraft("");
  }, [ticket?.ticketId]);

  useEffect(() => {
    setFocusedSupportTicket(ticket?.ticketId || null);
    return () => setFocusedSupportTicket(null);
  }, [ticket?.ticketId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length, ticket?.ticketId]);

  if (!ticket) {
    return (
      <section className="supportTicketThread supportTicketThread--empty">
        <p>Zgjidhni një ticket nga lista ose krijoni një të ri.</p>
      </section>
    );
  }

  const handleSend = async () => {
    if (isEmptyChatHtml(draft) || !canReply) return;
    const authorRole = isAdmin ? "admin" : "employee";
    const html = draft;
    try {
      const updated = await onSend(ticket, html, authorRole);
      if (updated) setDraft("");
    } catch {
      // Error toast is handled in useTicketMessages; keep draft for retry.
    }
  };

  return (
    <section className="supportTicketThread">
      <header className="supportTicketThread__header">
        <div>
          <h2>{ticket.ticketCategory}</h2>
          <p>
            {ticket.createdBy}
            {ticket.ticketPriority ? ` · ${ticket.ticketPriority}` : ""}
          </p>
        </div>
        <div className="supportTicketThread__actions">
          {isAdmin && (
            <Select
              value={ticket.ticketStatus}
              style={{
                minWidth: 160,
                ...ticketStatusStyle(ticket.ticketStatus),
              }}
              options={TICKET_STATUSES.map((s) => ({
                value: s.value,
                label: s.label,
              }))}
              onChange={(value) => onStatusChange(ticket, value)}
            />
          )}
          {isAdmin && !completed && (
            <MondayButton
              className="mondayButtonGrey"
              hasIcon={false}
              onClick={() => onComplete(ticket)}
            >
              Shënojeni si të rregulluar
            </MondayButton>
          )}
          {isAdmin && (
            <Popconfirm
              title="A doni ta fshihni problematikën?"
              onConfirm={() => onDelete(ticket)}
              okText="Po"
              cancelText="Jo"
            >
              <MondayButton className="mondayButtonRed" hasIcon={false}>
                Fshi
              </MondayButton>
            </Popconfirm>
          )}
        </div>
      </header>

      <div className="supportTicketThread__messages">
        {messages.map((message) => {
          const isOwn =
            message.authorRole === "admin"
              ? isAdmin
              : message.authorId === employeeId ||
                (!isAdmin && message.authorRole === "employee");
          return (
            <MessageBubble
              key={message.messageId}
              message={message}
              isOwn={isOwn}
            />
          );
        })}
        <div ref={bottomRef} />
      </div>

      <footer className="supportTicketThread__composer">
        {completed ? (
          <p className="supportTicketThread__closed">
            Ky ticket është i zgjidhur. Adminët mund ta rihapin nga statusi.
          </p>
        ) : (
          <ChatComposer
            value={draft}
            onChange={setDraft}
            onSend={handleSend}
            disabled={!canReply}
            sending={sending}
          />
        )}
      </footer>
    </section>
  );
}

export default TicketThread;
